import React, { useCallback, useMemo, useState } from 'react';

import { ScrollView, View } from 'react-native';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { StackNavigationProp } from '@react-navigation/stack';

import { RightArrowSettings } from 'assets/images';

import { LANGUAGE_KEYS } from 'config/languageKeys';
import gStyles from 'config/styles';

import { SCREEN } from 'router/screens';
import { RouteNavigationParams } from 'router/types';

import { selectAppState, selectLayoutConfig } from 'store/config/selectors';
import {
    getPinsStatusRequestErrorCode,
    getPinsStatusRequestErrorStatus,
} from 'store/user/selectors';

import { COLOR } from 'utils/colors';
import useHeaderConfig from 'utils/hooks/useHeaderConfig';
import { isWeb } from 'utils/responsiveness';

import ForgotPin from 'screens/Settings/ForgotPin';

import ErrorDialog from 'components/ErrorDialog';
import Notification from 'components/Notification';
import PinDialog from 'components/PinDialog';

import SettingsRowItem from '../components/RowItem';
import WebHeader from '../components/WebHeader';
import useChangePin from '../hooks/useChangePin';
import { SETTINGS_PARENTAL_CONTROL_AND_PIN } from '../languagesConst';
import { SimpleSettingsItem } from '../types';

type Props = {
    navigation: StackNavigationProp<
        RouteNavigationParams,
        SCREEN.PARENTAL_CONTROL_AND_PIN
    >;
};

const {
    SETTINGS_PARENTAL_CONTROL,
    SETTINGS_CHANGE_PIN_CODE,
    SETTINGS_FORGOT_PIN_CODE,
    UNSUCCESSFUL_PIN_CHANGE_TITLE,
    UNSUCCESSFUL_PIN_CHANGE_DESCRIPTION,
    OK_BUTTON,
} = LANGUAGE_KEYS;

function ParentalControl({ navigation }: Props) {
    const [forgotPinVisible, setForgotPinVisible] = useState(false);
    const [pinChangeErrorVisible, setPinChangeErrorVisible] = useState(false);
    const { t } = useTranslation();

    useHeaderConfig({ isRoot: isWeb, disableFocusOnMount: true });

    const appState = useSelector(selectAppState);
    const { background } = useSelector(selectLayoutConfig);
    const pinStatusErrorStatus = useSelector(getPinsStatusRequestErrorStatus);
    const pinStatusErrorCode = useSelector(getPinsStatusRequestErrorCode);

    const {
        pinDialogRef,
        changePinDialogVisible,
        changePinDialogStatus,
        pinChangedNotification,
        pinChangeFailure,
        onShowChangePinDialog,
        onCloseChangePinDialog,
        onNextPinEntered,
        onResetPinChangeNotification,
        onResetPinChangeFailure,
    } = useChangePin();

    const menuOptions = useMemo<SimpleSettingsItem[]>(
        () => [
            {
                id: SETTINGS_PARENTAL_CONTROL,
                title: t(SETTINGS_PARENTAL_CONTROL),
            },
            {
                id: SETTINGS_CHANGE_PIN_CODE,
                title: t(SETTINGS_CHANGE_PIN_CODE),
            },
            {
                id: SETTINGS_FORGOT_PIN_CODE,
                title: t(SETTINGS_FORGOT_PIN_CODE),
            },
        ],
        [t],
    );

    const handleChangePinDialogHide = useCallback(() => {
        if (pinChangeFailure) {
            setPinChangeErrorVisible(true);
        }
    }, [pinChangeFailure]);

    const handleClosePinChangeErrorDialog = useCallback(() => {
        onResetPinChangeFailure();
        setPinChangeErrorVisible(false);
    }, [onResetPinChangeFailure]);

    const handlePress = useCallback(
        ({ id }: SimpleSettingsItem) => {
            if (id === SETTINGS_PARENTAL_CONTROL) {
                navigation.navigate(SCREEN.PARENTAL_CONTROL);
            } else if (id === SETTINGS_CHANGE_PIN_CODE) {
                onShowChangePinDialog();
            } else if (id === SETTINGS_FORGOT_PIN_CODE) {
                setForgotPinVisible(true);
            }
        },
        [navigation, onShowChangePinDialog],
    );

    const closeForgotPinDialog = useCallback(() => {
        setForgotPinVisible(false);
    }, []);

    return (
        <>
            {isWeb && (
                <WebHeader
                    title={t(SETTINGS_PARENTAL_CONTROL_AND_PIN)}
                    disableFocusOnMount
                />
            )}
            <View
                style={[
                    gStyles.settingsContainer,
                    { backgroundColor: background },
                ]}
            >
                <ScrollView
                    style={[
                        gStyles.settingsContentContainer,
                        { backgroundColor: background },
                    ]}
                    nestedScrollEnabled
                    showsHorizontalScrollIndicator={false}
                    accessibilityRole="menu"
                >
                    {menuOptions.map((item, index) => (
                        <SettingsRowItem
                            key={item.id}
                            item={item}
                            title={item.title}
                            RightIcon={RightArrowSettings}
                            onPress={handlePress}
                            position={index + 1}
                            total={menuOptions.length}
                        />
                    ))}
                </ScrollView>
                <ForgotPin
                    visible={forgotPinVisible}
                    closeDialog={closeForgotPinDialog}
                />
                <PinDialog
                    ref={pinDialogRef}
                    appState={appState}
                    visible={changePinDialogVisible}
                    status={changePinDialogStatus}
                    onSubmit={onNextPinEntered}
                    onClose={onCloseChangePinDialog}
                    onHide={handleChangePinDialogHide}
                    haveToShowCloseButton
                />
                <ErrorDialog
                    visible={pinChangeErrorVisible}
                    title={UNSUCCESSFUL_PIN_CHANGE_TITLE}
                    description={UNSUCCESSFUL_PIN_CHANGE_DESCRIPTION}
                    actionButtonFillColor={COLOR.primary}
                    actionButtonTextStyle={{ color: COLOR.BLACK }}
                    actionButtonTitle={OK_BUTTON}
                    cancelButtonAccessibilityLabel={t(OK_BUTTON)}
                    actionButtonOnPress={handleClosePinChangeErrorDialog}
                    errorCode={`${pinStatusErrorCode || ''}`}
                    errorStatus={pinStatusErrorStatus}
                />
            </View>
            <Notification
                notification={pinChangedNotification}
                onResetNotification={onResetPinChangeNotification}
            />
        </>
    );
}

export default ParentalControl;
